<template>
    <article v-if="pending">
        <content-placeholders
            v-for="i in [0,1,2,3,4,5,6,7,8,9]"
            :key="i"
            style="margin-top: var(--spacing-xl);"
        >
            <content-placeholders-heading />
            <br><br>
            <content-placeholders-text :lines="6" />
            <content-placeholders-img />
            <content-placeholders-text :lines="3" />
            <br><br>
            <content-placeholders-text :lines="10" />
        </content-placeholders>
    </article>

    <div v-else-if="chapter && !error">
        <!-- eslint-disable vue/no-v-html -->
        <BaseGuidelineText
            ref="guideline-text"
            :content="chapterContent"
            :chapter-id="chapterId"
            @open-note="openNote"
        />
        <transition name="fade">
            <BaseGuidelineReferenceTooltip
                v-if="activeNoteNames"
                :guideline-id="guideline?.id"
                :names="activeNoteNames"
                @close="activeNoteNames = null"
            />
        </transition>
    </div>

    <div v-else>
        <h2>Not found</h2>
        <p>Sorry, this guideline chapter could not be found.</p>
    </div>
</template>

<script setup lang="ts">
import GuidelineChapterQuery from '~/graphql/queries/guidelines/chapter.graphql';

import {useGuidelineSearchStore} from '~/store/guideline-search';

const props = defineProps({
    guideline: {
        type: Object,
        required: false,
        default: null
    }
});

const {
    // searchQuery,
    currentChapterContent,
    // currentHighlightNumber,
    // currentChapterHighlightCount,
    // isFirstHighlightInChapter,
    // highlightOffset
} = storeToRefs(useGuidelineSearchStore());

const activeNoteNames = ref<null | string[]>(null);

const chapterId = computed(() => {
    return Number(chapter?.value?.id);
});

function openNote({ numbers }:{
    numbers: number[]
}) {
    if (numbers) {
        activeNoteNames.value = numbers.map(number => `note_${number}`);
    } else {
        activeNoteNames.value = null;
    }
}

const route = useRoute();
const { activeSite } = useMultisite();

const variables = computed(() => {
    return {
        uri: null,
        site: activeSite.value?.handle || '',
        slug: route.params.chapterSlug ? [route.params.chapterSlug] : [],
        guidelineId: props.guideline?.id ? Number(props.guideline.id) : null
    };
});

// We don't need the URI for this query
const { data, pending, error } = await useLazyAsyncQuery({
    query: GuidelineChapterQuery,
    variables
});

const chapter = computed(() => {
    if (!data.value?.chapter) {
        return null;
    }

    return data?.value?.chapter;
});


const chapterContent = computed(() => {
    const searchChapter = currentChapterContent.value;

    if (searchChapter) {
        return searchChapter;
    }

    return chapter?.value?.chapterContent;
});


const seoMeta = computed(() => {
    return useSeo(
        chapter.value?.seo || props.guideline?.seo,
        chapter.value || props.guideline,
        { seoTitle: props.guideline?.title }
    ).value;
});

useHead(() => seoMeta.value);

// Handle URL hash scrolling after content loads
function scrollToHash() {
    if (!route.hash) {
        return;
    }

    const el = document.getElementById(route.hash.replace('#', ''));
    if (el) {
        const offset = -150;
        window.scrollTo({ behavior: 'smooth', top: el.offsetTop + offset });
    }
}

watch(pending, async(isPending) => {
    if (!isPending && route.hash) {
        await nextTick();
        // Small delay to ensure DOM is fully rendered
        setTimeout(scrollToHash, 150);
    }
});

// Also watch route changes in case query is cached and pending doesn't change
watch(() => route.fullPath, async() => {
    if (!pending.value && route.hash) {
        await nextTick();
        setTimeout(scrollToHash, 150);
    }
});

</script>
